import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  cptCodesService,
  CreateCptCodeInput,
  UpdateCptCodeInput,
} from "../../services/admin/cptcodes.service";

export function useCptCodesAdmin(enabled = true) {
  return useQuery({
    queryKey: ["admin", "cpt-codes"],
    queryFn: cptCodesService.getAll,
    enabled,
  });
}

export function useCptCode(id?: string) {
  return useQuery({
    queryKey: ["admin", "cpt-codes", id],
    queryFn: () => cptCodesService.getById(id!),
    enabled: !!id,
  });
}

export function useCreateCptCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateCptCodeInput) => cptCodesService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "cpt-codes"] });
      queryClient.invalidateQueries({ queryKey: ["cpt-codes"] });
      notifications.show({
        title: "Success",
        message: "CPT Code created successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to create CPT Code",
        color: "red",
      });
    },
  });
}

export function useUpdateCptCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateCptCodeInput) => cptCodesService.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "cpt-codes"] });
      queryClient.invalidateQueries({ queryKey: ["cpt-codes"] });
      notifications.show({
        title: "Success",
        message: "CPT Code updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update CPT Code",
        color: "red",
      });
    },
  });
}

export function useDeleteCptCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => cptCodesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "cpt-codes"] });
      queryClient.invalidateQueries({ queryKey: ["cpt-codes"] });
      notifications.show({
        title: "Success",
        message: "CPT Code deleted successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to delete CPT Code",
        color: "red",
      });
    },
  });
}

export function useToggleCptCodeActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      cptCodesService.toggleActive(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "cpt-codes"] });
      queryClient.invalidateQueries({ queryKey: ["cpt-codes"] });
      notifications.show({
        title: "Success",
        message: "Status updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update status",
        color: "red",
      });
    },
  });
}
